import config from '../config';

const API_ENDPOINT = config.API_ENDPOINT;

/**
 * Service for managing date configuration from the backend API
 * Provides centralized date management with caching and error handling
 */

let dateConfigCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Today's date in Eastern time (YYYY-MM-DD), matching the dates returned by /config/dates
 * @returns {string}
 */
const getEasternToday = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};

/**
 * Fetch date configuration from the backend API
 * @returns {Promise<Object>} Date configuration object
 */
export const fetchDateConfiguration = async () => {
  try {
    console.log('Fetching date configuration from:', `${API_ENDPOINT}/config/dates`);
    const response = await fetch(`${API_ENDPOINT}/config/dates`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch date configuration: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Received date configuration:', data);
    
    // Cache the response
    dateConfigCache = data;
    cacheTimestamp = Date.now();
    
    return data;
  } catch (err) {
    console.error('Error fetching date configuration:', err);
    
    // Return cached data if available and not too old
    if (dateConfigCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION * 2)) {
      console.log('Using cached date configuration due to fetch error');
      return dateConfigCache;
    }
    
    // Fallback to default configuration if no cache available
    console.log('Using fallback date configuration');
    return getFallbackDateConfig();
  }
};

/**
 * Get cached date configuration if available and valid
 * @returns {Object|null} Cached date configuration or null
 */
export const getCachedDateConfiguration = () => {
  if (dateConfigCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return dateConfigCache;
  }
  return null;
};

/**
 * Get current year from date configuration
 * @param {Object} dateConfig - Date configuration object
 * @returns {number} Current ski sale year
 */
export const getCurrentYear = (dateConfig) => {
  return dateConfig ? dateConfig.currentYear : new Date().getFullYear();
};

/**
 * Format date string for display
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @param {string} format - Format type ('short', 'long', 'month-day')
 * @returns {string} Formatted date string
 */
export const getFormattedDate = (dateString, format = 'long') => {
  if (!dateString) return 'TBD';
  
  try {
    const date = new Date(dateString + 'T00:00:00'); // Ensure consistent timezone handling
    
    switch (format) {
      case 'short':
        return date.toLocaleDateString('en-US', { 
          month: 'numeric', 
          day: 'numeric', 
          year: 'numeric' 
        });
      case 'long':
        return date.toLocaleDateString('en-US', { 
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      case 'month-day':
        return date.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric' 
        });
      case 'month-day-year':
        return date.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        });
      default:
        return date.toLocaleDateString('en-US');
    }
  } catch (err) {
    console.error('Error formatting date:', dateString, err);
    return dateString;
  }
};

/**
 * Get date range string for display
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @param {string} format - Format type
 * @returns {string} Formatted date range
 */
export const getDateRange = (startDate, endDate, format = 'month-day-year') => {
  if (!startDate || !endDate) return 'TBD';
  
  const formattedStart = getFormattedDate(startDate, format);
  const formattedEnd = getFormattedDate(endDate, format);
  
  return `${formattedStart} - ${formattedEnd}`;
};

/**
 * Check if current date is within a specific period
 * @param {Object} dateConfig - Date configuration
 * @param {string} period - Period to check ('consignment', 'sale', 'pickup')
 * @returns {boolean} True if current date is within period
 */
export const isCurrentlyInPeriod = (dateConfig, period) => {
  if (!dateConfig) return false;
  
  const currentDate = getEasternToday();
  
  switch (period) {
    case 'consignment':
      return currentDate >= dateConfig.consignmentOpenDate && currentDate <= dateConfig.consignmentCloseDate;
    case 'dropoff':
      return currentDate >= dateConfig.dropoffDate && currentDate <= (dateConfig.dropoffEndDate || dateConfig.dropoffDate);
    case 'sale':
      return currentDate >= dateConfig.saleStartDate && currentDate <= dateConfig.saleEndDate;
    case 'pickup':
      return currentDate >= dateConfig.pickupStartDate && currentDate <= dateConfig.pickupEndDate;
    default:
      return false;
  }
};

/**
 * Get the current active period
 * @param {Object} dateConfig - Date configuration
 * @returns {string} Current period ('pre-consignment', 'consignment', 'pre-dropoff', 'dropoff', 'sale', 'pickup', 'closed', 'unknown')
 */
export const getCurrentPeriod = (dateConfig) => {
  if (!dateConfig || !dateConfig.consignmentOpenDate) return 'unknown';
  
  const currentDate = getEasternToday();
  
  if (currentDate < dateConfig.consignmentOpenDate) {
    return 'pre-consignment';
  } else if (isCurrentlyInPeriod(dateConfig, 'consignment')) {
    return 'consignment';
  } else if (currentDate < dateConfig.dropoffDate) {
    return 'pre-dropoff';
  } else if (isCurrentlyInPeriod(dateConfig, 'dropoff')) {
    return 'dropoff';
  } else if (isCurrentlyInPeriod(dateConfig, 'sale')) {
    return 'sale';
  } else if (isCurrentlyInPeriod(dateConfig, 'pickup')) {
    return 'pickup';
  } else {
    return 'closed';
  }
};

/**
 * Fallback date configuration when API is unavailable
 * @returns {Object} Default date configuration
 */
export const getFallbackDateConfig = () => {
  // Dates are unknown without the API; null dates display as "TBD"
  return {
    currentYear: Number(getEasternToday().slice(0, 4)),
    dropoffDate: null,
    dropoffEndDate: null,
    consignmentOpenDate: null,
    consignmentCloseDate: null,
    saleStartDate: null,
    saleEndDate: null,
    pickupStartDate: null,
    pickupEndDate: null,
    lastUpdated: new Date().toISOString()
  };
};

/**
 * Clear the date configuration cache
 */
export const clearCache = () => {
  dateConfigCache = null;
  cacheTimestamp = null;
};

export default {
  fetchDateConfiguration,
  getCachedDateConfiguration,
  getCurrentYear,
  getFormattedDate,
  getDateRange,
  isCurrentlyInPeriod,
  getCurrentPeriod,
  clearCache
};